import type { ValueAnimationTransition } from "motion/react"
import type { ConstellationBackgroundLayerItemProps } from "./ConstellationBackgroundLayerItem"

const getRandomDelay = () => -(Math.random() * 0.7 + 0.05)
const randomDuration = () => Math.random() * 0.07 + 0.23

export const LOOP_DURATION = 800
export const TRANSITION_DURATION = 1.25

export function useConstellationBackgroundConstants({
  blur,
  scaleOffset = 0,
  index,
  delay = 0,
}: { delay?: number } & Pick<
  ConstellationBackgroundLayerItemProps,
  "blur" | "scaleOffset" | "index"
>) {
  const initialState = {
    opacity: 0,
    offsetPath: "ellipse(15% 10%)",
    offsetDistance: "-10%",
    scale: 0.25,
    skewX: 1,
    skewY: 1,
    rotate: 0,
    translate: 0,
    ...(blur && {
      filter: "blur(75px)",
    }),
  }

  const loopAnimation = {
    offsetDistance: ["100%", "0%"],
    scale: [1.1, 0.25, 1.1, 0.25, 1.1].map(scale => scale + scaleOffset),
    skewX: [2, 1, -2, 1, 2],
    skewY: [2, 1, -2, 1, 2],
    ...(blur && {
      filter: [
        "blur(0px)",
        "blur(0px)",
        "blur(75px)",
        "blur(0px)",
        "blur(0px)",
        "blur(75px)",
        "blur(0px)",
        "blur(0px)",
      ],
    }),
  }

  const loopAnimationOptions: ValueAnimationTransition = {
    duration: LOOP_DURATION,
    delay,
    repeat: Number.POSITIVE_INFINITY,
    ease: "linear",
  }

  const isEven = index % 2 === 0
  const wiggleAnimation = isEven
    ? {
        rotate: [0, 1.3, 0],
        translate: [0, 1.3, 0],
      }
    : {
        rotate: [0, 0.4, 0],
        translate: [0, -1.4, 0],
      }

  const wiggleAnimationOptions: ValueAnimationTransition = {
    duration: randomDuration() * 10,
    delay: getRandomDelay(),
    repeat: Number.POSITIVE_INFINITY,
  }

  const transitionAnimationOptions: ValueAnimationTransition = {
    duration: 1.5,
    delay: index / 25,
    ease: "circInOut",
  }

  return {
    initialState,
    loopAnimation,
    loopAnimationOptions,
    wiggleAnimation,
    wiggleAnimationOptions,
    transitionAnimationOptions,
  }
}
